import {createAction, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {CardStore} from "./cards.interface";
import {getActionName} from "../utils/getActionName";
import {CardGenerationDto, CardMarkDto, CardState, LlmProvider} from "../common";

const initialState : Record<string, CardStore> = {};
const sliceName = "cards";

const cardsSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
        put(state, action: PayloadAction<CardStore[]>) {
            for(const cardDto of action.payload){
                state[cardDto.cardId] = cardDto;
            }
        },

        setMarkup(state, action: PayloadAction<{cardId: string, markup: string}>){
            if (state[action.payload.cardId] !== undefined)
                state[action.payload.cardId].markup = action.payload.markup;
        },

        setState(sliceState, action: PayloadAction<SetStatePayload>){
            const {cardId, state} = action.payload;

            if (sliceState[cardId] !== undefined)
                sliceState[cardId].state = state;
        },

        setGeneration(state, action: PayloadAction<SetGenerationPayload>) {
            const {cardId, generation} = action.payload;

            if (state[cardId] !== undefined)
                state[cardId].generation = generation;
        },

        setMark(state, action: PayloadAction<SetMarkPayload>) {
            const {cardId, mark} = action.payload;

            if (state[cardId] !== undefined)
                state[cardId].mark = mark;
        },

        deleteFromStore(state, action: PayloadAction<string>) {
            delete state[action.payload];
        }
    }
});

export interface SetStatePayload {
    cardId: string;
    state: CardState;
}

export interface SetGenerationPayload {
    cardId: string;
    generation?: CardGenerationDto;
}

export interface SetMarkPayload {
    cardId: string;
    mark?: CardMarkDto;
}

export interface MarkCardPayload {
    cardId: string;
    note: string;
}

export interface MarkSubcardPayload {
    cardId: string;
    subcardKey: string;
    note: string;
}

export interface UnmarkSubcardPayload {
    cardId: string;
    subcardKey: string;
}

export interface RegenerateCardPayload {
    cardId: string;
    text?: string;
    instructions?: string;
    provider?: LlmProvider;
}

export const cardsReducer = cardsSlice.reducer;

export interface AddTagPayload {
    cardId: string;
    tag: string;
}

export const cardsActions = {
    ...cardsSlice.actions,
    loadCard: createAction<string>(getActionName(sliceName, 'loadCard')),
    deleteCard: createAction<string>(getActionName(sliceName, 'deleteCard')),
    regenerateCard: createAction<RegenerateCardPayload>(getActionName(sliceName, 'regenerateCard')),
    invertCard: createAction<string>(getActionName(sliceName, 'invertCard')),
    revertCard: createAction<string>(getActionName(sliceName, 'revertCard')),
    moveToState: createAction<SetStatePayload>(getActionName(sliceName, 'moveToState')),
    markCard: createAction<MarkCardPayload>(getActionName(sliceName, 'markCard')),
    /** Removes only the whole-card mark; marked subcards stay. */
    unmarkWholeCard: createAction<string>(getActionName(sliceName, 'unmarkWholeCard')),
    markSubcard: createAction<MarkSubcardPayload>(getActionName(sliceName, 'markSubcard')),
    unmarkSubcard: createAction<UnmarkSubcardPayload>(getActionName(sliceName, 'unmarkSubcard')),
    /** Removes every mark of the cards, whole-card and subcard ones. */
    unmarkCards: createAction<string[]>(getActionName(sliceName, 'unmarkCards')),
    addTag: createAction<AddTagPayload>(getActionName(sliceName, 'addTag')),
};
